import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import { actionTypeSelector, authSelector, equipmentSelector, equipmentsSelector } from "../../reducers";
import { getLocaleText, removeGridData } from "../../actions/datagrid";
import { dateFormatter, dateGetter, fieldGetter } from "../../common/utils";
import {
  defaultColDef,
  gridSettings,
  sideBar,
  statusBar,
  saveViewState,
  loadViewState,
  restoreViewState,
  getRowId,
  contextMenu,
} from "../../common/grid";
import { AddEquipment } from "./equipmentmodal";

import { pencilIconString, restoreIconString, filesIconString } from "../../common/icons";
import { getPersonEquipments } from "../../actions/equipment/equipmentsgetallforperson";
import { getAllEquipments } from "../../actions/equipment/equipmentsgetall";
import { getEquipment } from "../../actions/equipment/equipmentget";
import { deleteEquipment } from "../../actions/equipment/equipmentdelete";
import { postEquipment } from "../../actions/equipment/equipmentpost";
import { LoadingSuspense } from "../../common/loaders/loadingsuspense";
import { PdfReport } from "../../common/pdfreport";
import { fetchSettings } from "../../common/settings";
import { useQuery } from "@tanstack/react-query";
import { currentUserPersonQuery } from "../../api";
import Switch from "../../common/Switch";

const viewStateKey = "equipmentViewState";
let copy = false;

const initialState = {
  columnVisibility: {
    hiddenColIds: ["deliveryDate", "returnDate"],
  },
};

const filteredColumn = "returnDate";

export default function EquipmentView({ isPersonal }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { companyName } = useSelector(authSelector);
  const equipment = useSelector(equipmentSelector);
  const equipments = useSelector(equipmentsSelector);
  const actionType = useSelector(actionTypeSelector);

  const [settings, setSettings] = useState({});

  const [report, setReport] = useState(null);
  const closeReport = () => setReport(null);

  useEffect(() => {
    (async () => {
      if (!isPersonal) {
        setSettings(await fetchSettings());
        dispatch(getAllEquipments(true));
      }
    })();

    return () =>
      dispatch({
        type: "CLEAR_EQUIPMENTS",
        payload: { equipments: null },
      });
  }, []);

  const [modalShow, setModalShow] = useState(null);

  const person = useQuery(currentUserPersonQuery(isPersonal)).data;

  useEffect(() => {
    if (isPersonal) {
      if (person?.personId) {
        dispatch(getPersonEquipments(person?.personId));
      }
    }
  }, [person]);

  useEffect(() => {
    if (actionType === "POST_EQUIPMENT_SUCCEEDED") {
      copy = false;
      dispatch(getAllEquipments(true));
      setModalShow(null);
    }
    if (actionType === "POST_EQUIPMENT_EDIT_SUCCEEDED") {
      dispatch(getAllEquipments(true));
    }
    if (actionType === "UPLOAD_DONE") {
      setModalShow(null);
    }
    if (actionType === "GET_EQUIPMENT_SUCCEEDED") {
      setModalShow({ copy });
    }
    if (actionType === "DELETE_EQUIPMENT_SUCCEEDED") {
      removeGridData(gridRef, [equipment]);
    }
  }, [actionType]);

  const saveData = (data) => dispatch(postEquipment(data));

  const removeEquipment = (id) => {
    if (confirm(t("DeleteEquipmentConfirm", "Haluatko varmasti poistaa välineistön/käyttöoikeuden?"))) {
      dispatch(deleteEquipment(id));
    }
  };

  const setShow = (newState) => {
    if (newState) setModalShow({});
    else {
      setModalShow(null);
      copy = false;
    }
  };

  const gridRef = useRef();

  const columnDefs = [
    {
      headerName: t("PersonNumber", "Henkilönumero"),
      field: "personNumber",
      filter: "agNumberColumnFilter",
    },
    {
      headerName: t("Name", "Nimi"),
      field: "personName",
    },
    {
      headerName: t("JobTitle", "Tehtävänimike"),
      field: "profession",
    },
    {
      headerName: t("EquipmentOrAccessRight", "Väline/käyttöoikeus"),
      field: "equipmentCodeItem",
      valueGetter: (params) => {
        const codeItem = fieldGetter(params);
        if (!codeItem) return null;
        return codeItem.companyUD === 0 ? t(codeItem.codeValue) : codeItem.codeValue;
      },
      width: 250,
    },
    {
      headerName: t("Description", "Kuvaus"),
      field: "description",
      width: 350,
    },
    {
      headerName: t("Code", "Tunniste"),
      field: "code",
    },
    {
      headerName: t("OrderDate", "Tilauspvm."),
      cellClass: "dateType",
      field: "orderDate",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
      width: 175,
    },
    {
      headerName: t("DueDate", "Eräpvm."),
      cellClass: "dateType",
      field: "dueDate",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
      width: 150,
    },
    {
      headerName: t("DeliveryDate", "Luovutus pvm."),
      cellClass: "dateType",
      field: "deliveryDate",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
      width: 175,
    },
    {
      headerName: t("ReturnDate", "Palautus pvm."),
      cellClass: "dateType",
      field: "returnDate",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
      width: 175,
    },
    {
      headerName: t("ReturnedBy", "Palautettava/suljettava viimeistään"),
      cellClass: "dateType",
      field: "returningDate",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
      width: 175,
    },
    {
      field: "supervisor",
      headerName: t("Supervisor", "Esihenkilö"),
    },
    {
      field: "supervisor2",
      headerName: `${t("Supervisor", "Esihenkilö")} 2`,
    },
    {
      field: "dim1",
      headerName: settings.dim1name || t("Dimension1", "Kustannuspaikka"), // costUnitName
    },
    {
      field: "dim2",
      headerName: settings.dim2name || t("Dimension2", "Projekti"), // projectName
    },
    {
      field: "dim3",
      headerName: settings.dim3name || t("Dimension3", "Dimensio 3"),
    },
    {
      field: "dim4",
      headerName: settings.dim4name || t("Dimension4", "Dimensio 4"),
    },
    {
      field: "dim5",
      headerName: settings.dim5name || t("Dimension5", "Dimensio 5"),
    },
    {
      field: "dim6",
      headerName: t("BusinessId", "Y-tunnus"),
    },
  ];

  const doubleClickAction = (row) => {
    if (isPersonal) {
      return;
    }
    if (!isNaN(row.node.id)) {
      dispatch(getEquipment(row.data.id));
    }
  };

  const getContextMenuItems = useCallback((params) => {
    let items = [];

    const isPivot = params.api.isPivotMode();

    items.push({
      name: t("DefaultGridValues", "Palauta taulukon oletusasetukset"),
      action: function () {
        restoreViewState(viewStateKey, initialState);
      },
      icon: restoreIconString,
    });

    if (!isPivot && !isPersonal) {
      items.push("separator");

      items.push(
        {
          name: t("Edit", "Muokkaa"),
          icon: pencilIconString,
          action: () => dispatch(getEquipment(params.node.id)),
        },
        {
          name: t("CopyEquipment", "Kopioi välineistö uuden pohjaksi"),
          action: () => {
            copy = true;
            dispatch(getEquipment(params.node.id));
          },
          icon: filesIconString,
        }
      );
    }

    items.push("separator");

    items = items.concat(
      contextMenu(t, params, setReport, {
        title: t("EquipmentAndAccessRights", "Välineistö ja käyttöoikeudet"),
        companyname: companyName,
      }),
    );

    return items;

  }, [t, isPersonal, dispatch, companyName]);

  const showReturned = (e) => {
    const { api } = gridRef.current;

    if (e.target?.checked) {
      api.setColumnFilterModel(filteredColumn, null);
    } else {
      api.setColumnFilterModel(filteredColumn, {
        filterType: "date",
        type: "blank",
        filter: false,
      });
    }

    api.onFilterChanged();
  };

  return (
    <>
      {report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
      {actionType === "GET_EQUIPMENT_LOADING" && <LoadingSuspense />}

      <Row className="my-1">
        <Col sm="4">
          <h2 className="viewheading">{t("EquipmentAndAccessRights", "Välineistö ja käyttöoikeudet")}</h2>
          <Switch className="mb-3" label={t("ShowReturned", "Näytä palautetut")} onChange={showReturned} />
        </Col>
        <Col sm="8" align="right">
          <Row className="g-2">
            <Col />
            <Col md="auto">
              <AddEquipment
                equipmentData={equipment}
                saveData={saveData}
                show={modalShow}
                setShow={setShow}
                person={person}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
        <AgGridReact
          {...gridSettings}
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          sideBar={sideBar}
          rowData={equipments}
          rowClassRules={{
            endedRow: ({ data }) => !!data?.returnDate,
          }}
          statusBar={statusBar}
          onRowDoubleClicked={doubleClickAction}
          getRowId={getRowId}
          getLocaleText={(params) => getLocaleText(t, params)}
          getContextMenuItems={getContextMenuItems}
          animateRows
          suppressCellFocus
          enableCellTextSelection
          initialState={loadViewState(viewStateKey) || initialState}
          onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
          onGridReady={showReturned}
          debug={import.meta.env.DEV}
        />
      </Row>
    </>
  );
}
